import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EstimatesV2Service } from '../../../core/services/estimateV2.service';
import { FormStatus, QuoteRequestRecipient, QuoteRequestRecipientUpdateModelInput, QuoteStatus } from '../../../data/models';
import { RepoQuoteRequestsService } from '../../../data/repositories/repo-quoterequests.service';
import { LayoutService } from '../../../pages/layout/layout.service';
import { BxLogger } from '../../services/bx-logger.service';
import { DialogParams, DialogService } from '../../services/dialog.service';
import { BxLifeCycleInput } from '../bx-lifecycle';
import { NavListItem, RFQResponseComponents, RfqResponseModalResult } from './rfq-response-side-form.model';

@UntilDestroy()
@Component({
    selector: 'app-rfq-response-side-form',
    templateUrl: './rfq-response-side-form.component.html',
    styleUrls: ['./rfq-response-side-form.component.scss'],
    standalone: false,
})
export class RfqResponseSideFormComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() quoteRequestId: string;
    // @ts-ignore TS2564
    @Input() quoteRequestRecipientId: string;
    @Output() closemodal = new EventEmitter<RfqResponseModalResult>();

    // @ts-ignore TS2564
    recipient: QuoteRequestRecipient;
    // @ts-ignore TS2564
    quoteRequestRecipient: QuoteRequestRecipient;
    // @ts-ignore TS2564
    selectedNavItem: string;
    rfqResponseComponents = RFQResponseComponents;
    requestStatus = '';
    formStatus = FormStatus.Idle;

    navList: NavListItem[] = [
        {
            label: 'Details',
            value: RFQResponseComponents.Details,
            iconClass: 'ti-bell',
            isSelected: true,
            isValid: true,
            isRequired: true,
            isEdited: false,
            secondaryLabel: '',
        },
        {
            label: 'Costings',
            value: RFQResponseComponents.CostingItems,
            iconClass: 'icon-ic-clients-s1',
            isSelected: false,
            isValid: false,
            isRequired: true,
            isEdited: false,
            secondaryLabel: '',
        },
        {
            label: 'Messages',
            value: RFQResponseComponents.Messages,
            iconClass: 'icon-ic-emails-s1',
            isSelected: false,
            isValid: true,
            isRequired: true,
            isEdited: false,
            secondaryLabel: '',
        },
        {
            label: 'Attachments',
            value: RFQResponseComponents.Attachments,
            iconClass: 'icon-ic-attachment-s1',
            isSelected: false,
            isValid: true,
            isRequired: true,
            isEdited: false,
            secondaryLabel: '',
        },
    ];

    requestStates = {
        states: [
            {
                value: QuoteStatus.Notified,
                displayValue: 'Notified',
                activeHoverClass: 'orange',
                helpTemplate: null!,
                locked: true,
            },
            {
                value: QuoteStatus.Opened,
                displayValue: 'Opened',
                activeHoverClass: 'orange',
                helpTemplate: null!,
                locked: true,
            },
            {
                value: QuoteStatus.Declined,
                displayValue: 'Declined',
                activeHoverClass: 'orange',
                helpTemplate: null!,
            },
            {
                value: QuoteStatus.Pending,
                displayValue: 'Pending',
                activeHoverClass: 'orange',
                helpTemplate: null!,
            },
            {
                value: QuoteStatus.Quoting,
                displayValue: 'Quoting',
                activeHoverClass: 'orange',
                helpTemplate: null!,
            },
            {
                value: QuoteStatus.Submitted,
                displayValue: 'Submitted',
                activeHoverClass: 'orange',
                helpTemplate: null!,
            },
            {
                value: QuoteStatus.Accepted,
                displayValue: 'Accepted',
                activeHoverClass: 'green',
                helpTemplate: null!,
                locked: true,
            },
        ],
        name: '',
        locked: false,
        serverErrors: [],
    } satisfies BxLifeCycleInput;

    constructor(
        public layoutService: LayoutService,
        private logger: BxLogger,
        private quoteReq: RepoQuoteRequestsService,
        private bsModalRef: BsModalRef,
        private estimateV2Service: EstimatesV2Service,
        private dialogService: DialogService
    ) {}

    ngOnInit(): void {
        // @ts-ignore TS2345
        this.switchForm(this.navList[0]);

        this.estimateV2Service
            .getQuoteRequestRecipient(this.quoteRequestId, this.quoteRequestRecipientId)
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((response) => {
                const recipients: QuoteRequestRecipient[] = response.data.quoteRequestRecipients.nodes;
                if (recipients.length > 0) {
                    this.recipient = JSON.parse(JSON.stringify(recipients[0]));
                    this.quoteRequestRecipient = JSON.parse(JSON.stringify(recipients[0]));
                    this.requestStatus = this.recipient.quoteStatus;
                }
            });
    }

    switchForm(form: NavListItem) {
        this.selectedNavItem = form.value;
    }

    cancel() {
        // @ts-ignore TS2322
        this.closemodal.emit({ cancelled: true, updatedRecipient: null });
        this.bsModalRef.hide();
    }

    saveAndClose() {
        this.formStatus = FormStatus.Saving;
        const updateRecipient: QuoteRequestRecipientUpdateModelInput = {
            iD_QuoteRequestRecipient: this.recipient.iD_QuoteRequestRecipient,
            quoteStatus: this.requestStatus,
            quoteRequestRecipientItems: this.recipient.quoteRequestRecipientItems.map((item) => ({
                iD_QuoteRequestRecipientItem: item.iD_QuoteRequestRecipientItem,
                total: item.total,
                tax: item.tax,
                totalIncTax: item.totalIncTax,
                isSelected: item.isSelected,
                isRecipe: item.isRecipe,
                taxStatus: item.taxStatus,
                taxRates: item.taxRates,
                quoteRequestRecipientRecipeItems: item.quoteRequestRecipientRecipeItems.map((recipeItem) => ({
                    iD_QuoteRequestRecipientRecipeItem: recipeItem.iD_QuoteRequestRecipientRecipeItem,
                    total: recipeItem.total,
                    tax: recipeItem.tax,
                    totalIncTax: recipeItem.totalIncTax,
                    isSelected: recipeItem.isSelected,
                    taxStatus: recipeItem.taxStatus,
                    taxRates: recipeItem.taxRates,
                })),
            })),
        };

        // @ts-ignore TS2532
        const itemMap = new Map(this.recipient.quoteRequestRecipientItems.map((item) => [item.iD_QuoteRequestRecipientItem, item]));
        const hasItemPriceChanged = this.quoteRequestRecipient?.quoteRequestRecipientItems?.some(
            // @ts-ignore TS2532
            (item) => itemMap.get(item.iD_QuoteRequestRecipientItem).total !== item.total
        );
        if (hasItemPriceChanged && this.requestStatus === this.recipient.quoteStatus) {
            updateRecipient.quoteStatus = QuoteStatus.Submitted;
        }

        this.estimateV2Service.updateQuoteRequestRecipient(updateRecipient).subscribe((response) => {
            this.formStatus = FormStatus.Saved;
            this.logger.success('Items costs updated');
            this.closemodal.emit({
                cancelled: false,
                // @ts-ignore TS2533
                updatedRecipient: response.data.updateQuoteRequestRecipientV3,
            });
            this.bsModalRef.hide();
        });

        if (hasItemPriceChanged) {
            const params: DialogParams = {
                title: 'Quote modified successfully',
                text: `<p>Do you want to inform the recipient of the changes made? <br> An email will be sent to ${this.recipient.email}</p>`,
                type: 'success',
                allowHtml: true,
                cancelText: "Don't send",
                okText: 'Send email confirmation',
            };
            this.dialogService.show(params).then(
                () => {
                    this.quoteReq.Modify(this.recipient.iD_QuoteRequest, this.recipient.iD_QuoteRequestRecipient);
                },
                () => ({})
            );
        }
    }

    countUpdated(index: number, count: number) {
        // @ts-ignore TS2532
        this.navList[index].secondaryLabel = count.toString();
    }
}
